import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { tenantSlug, TenantContext } from "./tenant";
import api from "./lib/api";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import StudentsPage from "./pages/StudentsPage";
import QRCodesPage from "./pages/QRCodesPage";
import ReportsPage from "./pages/ReportsPage";
import ScanPage from "./pages/ScanPage";
import ClientsPage from "./pages/ClientsPage";
import UsersPage from "./pages/UsersPage";
import AlertsPage from "./pages/AlertsPage";

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-900" />
      </div>
    );
  if (user === false) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role))
    return <Navigate to={user.role === "provider" ? "/scan" : "/"} replace />;
  return children;
}

const ADMIN_ROLES = ["super_admin", "client_admin"];

function App() {
  const [tenant, setTenant] = useState(tenantSlug ? undefined : null);

  useEffect(() => {
    if (!tenantSlug) return;
    api.get("/branding", { params: { slug: tenantSlug } })
      .then((r) => {
        if (r.data?.found) setTenant(r.data);
        else window.location.replace("/");
      })
      .catch(() => window.location.replace("/"));
  }, []);

  if (tenant === undefined)
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50" data-testid="tenant-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-900" />
      </div>
    );

  return (
    <TenantContext.Provider value={tenant}>
      <AuthProvider>
        <BrowserRouter basename={tenant ? `/${tenantSlug}` : "/"}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/scan" element={<Protected roles={["provider"]}><ScanPage /></Protected>} />
          <Route element={<Protected><Layout /></Protected>}>
            <Route path="/" element={<Protected roles={ADMIN_ROLES}><DashboardPage /></Protected>} />
            <Route path="/students" element={<StudentsPage />} />
            <Route path="/qr-codes" element={<Protected roles={ADMIN_ROLES}><QRCodesPage /></Protected>} />
            <Route path="/reports" element={<Protected roles={ADMIN_ROLES}><ReportsPage /></Protected>} />
            <Route path="/clients" element={<Protected roles={["super_admin", "client_admin"]}><ClientsPage /></Protected>} />
            <Route path="/users" element={<Protected roles={["super_admin", "client_admin"]}><UsersPage /></Protected>} />
            <Route path="/alerts" element={<AlertsPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </TenantContext.Provider>
  );
}

export default App;
